import { useState, useEffect, useRef, useCallback } from 'react';

export function useWebSocket(serverId) {
  const [connected, setConnected] = useState(false);
  const [consoleLines, setConsoleLines] = useState([]);
  const [serverStatus, setServerStatus] = useState(null);
  const [chatEntries, setChatEntries] = useState([]);
  const [players, setPlayers] = useState([]);
  const [chatWatchActive, setChatWatchActive] = useState(false);
  const [whitelistNotifications, setWhitelistNotifications] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const chatWatchPingTimer = useRef(null);

  const connect = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host; // includes port if non-standard (e.g. localhost:3001)
    const url = `${protocol}//${host}/ws`;

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      if (serverId) {
        ws.send(JSON.stringify({ type: 'subscribe', serverId }));
      }
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);

      switch (msg.type) {
        case 'history':
          setConsoleLines(msg.lines || []);
          break;
        case 'console':
          setConsoleLines(prev => [...prev.slice(-999), { timestamp: msg.timestamp, text: msg.text }]);
          break;
        case 'status':
        case 'statusUpdate':
          setServerStatus(msg.status);
          break;
        case 'chat':
          if (msg.entry) {
            setChatEntries(prev => [...prev.slice(-499), msg.entry]);
          }
          break;
        case 'playerJoin':
          setPlayers(prev => {
            if (prev.includes(msg.player)) return prev;
            return [...prev, msg.player];
          });
          break;
        case 'playerLeave':
          setPlayers(prev => prev.filter(p => p !== msg.player));
          break;
        case 'chatWatchStarted':
          setChatWatchActive(true);
          break;
        case 'chatWatchStopped':
          setChatWatchActive(false);
          break;
        case 'whitelistRequest':
        case 'whitelistApproved':
          setWhitelistNotifications(prev => [...prev.slice(-49), msg]);
          break;
        default:
          break;
      }
    };

    ws.onclose = () => {
      setConnected(false);
      setChatWatchActive(false);
      // Only reconnect if this is still the active WS (prevent ghost reconnects)
      if (wsRef.current === ws) {
        reconnectTimer.current = setTimeout(connect, 3000);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [serverId]);

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (chatWatchPingTimer.current) clearInterval(chatWatchPingTimer.current);
    };
  }, [connect]);

  // Subscribe to a different server
  useEffect(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && serverId) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', serverId }));
      setConsoleLines([]);
      setChatEntries([]);
      setPlayers([]);
    }
  }, [serverId]);

  const sendCommand = useCallback((command) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && serverId) {
      wsRef.current.send(JSON.stringify({ type: 'command', serverId, command }));
    }
  }, [serverId]);

  // Initialize players from server data (for when WS hasn't caught up yet)
  const initPlayers = useCallback((playerList) => {
    setPlayers(playerList || []);
  }, []);

  // Initialize chat from API history
  const initChatHistory = useCallback((entries) => {
    setChatEntries(entries || []);
  }, []);

  // Start watching chat for a server (sends join notification in-game)
  const startChatWatch = useCallback((watchServerId, username) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'chatWatch',
        serverId: watchServerId,
        username,
      }));

      // Start keepalive ping every 30s
      if (chatWatchPingTimer.current) clearInterval(chatWatchPingTimer.current);
      chatWatchPingTimer.current = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'chatWatchPing' }));
        }
      }, 30000);
    }
  }, []);

  // Stop watching chat (sends leave notification in-game)
  const stopChatWatch = useCallback(() => {
    if (chatWatchPingTimer.current) {
      clearInterval(chatWatchPingTimer.current);
      chatWatchPingTimer.current = null;
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'chatUnwatch' }));
    }
    setChatWatchActive(false);
  }, []);

  // Clear whitelist notifications
  const clearWhitelistNotifications = useCallback(() => {
    setWhitelistNotifications([]);
  }, []);

  return {
    connected,
    consoleLines,
    serverStatus,
    chatEntries,
    players,
    chatWatchActive,
    whitelistNotifications,
    sendCommand,
    initPlayers,
    initChatHistory,
    startChatWatch,
    stopChatWatch,
    clearWhitelistNotifications,
  };
}
